// useDashboardCache.js
// Place this file in:  src/hooks/useDashboardCache.js

import { useCallback, useEffect, useRef, useState } from "react";

const CACHE_KEY_PREFIX = "dashboardCache_";
const CACHE_TTL_MS     = 5 * 60 * 1000; // 5 minutes

function readCache(key) {
  try {
    const raw = sessionStorage.getItem(CACHE_KEY_PREFIX + key);
    if (!raw) return null;
    const { data, ts } = JSON.parse(raw);
    if (Date.now() - ts > CACHE_TTL_MS) {
      sessionStorage.removeItem(CACHE_KEY_PREFIX + key);
      return null;
    }
    return data;
  } catch {
    return null;
  }
}

function writeCache(key, data) {
  try {
    sessionStorage.setItem(
      CACHE_KEY_PREFIX + key,
      JSON.stringify({ data, ts: Date.now() })
    );
  } catch {
    // storage full — not fatal
  }
}

/**
 * useDashboardCache
 *
 * @param {string}   cacheKey   – unique key per filter combination
 * @param {Function} fetchFn    – async () => data
 * @param {number}   debounceMs – default 400
 *
 * @returns {{ data, isLoading, isRefreshing, error, refetch }}
 *   isLoading    – true ONLY when there is no cached data at all (genuine first load)
 *   isRefreshing – true during a silent background refresh (cache exists, updating quietly)
 */
export function useDashboardCache(cacheKey, fetchFn, debounceMs = 400) {
  const [data,         setData]         = useState(() => readCache(cacheKey));
  const [isLoading,    setIsLoading]    = useState(() => readCache(cacheKey) === null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [error,        setError]        = useState(null);

  const fetchFnRef    = useRef(fetchFn);
  const isFetching    = useRef(false);
  const debounceTimer = useRef(null);
  const latestKey     = useRef(cacheKey);

  fetchFnRef.current = fetchFn;
  latestKey.current  = cacheKey;

  const doFetch = useCallback(async (key) => {
    if (isFetching.current) return;
    isFetching.current = true;

    const cached   = readCache(key);
    const hasCache = cached !== null;

    if (!hasCache) {
      setIsLoading(true);
    } else {
      setData(cached);
      setIsRefreshing(true);
    }

    try {
      const fresh = await fetchFnRef.current();
      if (key !== latestKey.current) return;
      if (fresh !== null && fresh !== undefined) {
        writeCache(key, fresh);
        setData(fresh);
      }
      setError(null);
    } catch (err) {
      if (key === latestKey.current) {
        setError(err?.message || "Failed to fetch");
      }
    } finally {
      isFetching.current = false;
      if (key === latestKey.current) {
        setIsLoading(false);
        setIsRefreshing(false);
      }
    }
  }, []);

  useEffect(() => {
    const cached = readCache(cacheKey);
    if (cached) {
      setData(cached);
      setIsLoading(false);
    } else {
      setIsLoading(true);
    }

    clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => {
      doFetch(cacheKey);
    }, debounceMs);

    return () => clearTimeout(debounceTimer.current);
  }, [cacheKey, debounceMs, doFetch]);

  const refetch = useCallback(() => {
    isFetching.current = false;
    doFetch(latestKey.current);
  }, [doFetch]);

  return { data, isLoading, isRefreshing, error, refetch };
}
